import React, {Component} from 'react';
import {injectIntl} from 'react-intl';
import {graphql} from '@apollo/client/react/hoc/graphql';
import {inject, observer} from 'mobx-react';
import {observable, action, makeObservable} from 'mobx';
import InfoModal from 'components/InfoModal';
import Spinner from 'components/Spinner';
import * as Sentry from '@sentry/react';

import {InstructionOverview} from 'api/instructions/queries';
import {getInstructionOverviewOptions} from 'api/instructions/query-options';

import {SubmitChecklistAnswer} from 'api/checklists/mutations';
import {submitChecklistAnswerOptions} from 'api/checklists/mutation-options';
import {commonPalette} from 'styles/palette';
import {CHECKLIST_EXTRA_FIELD_VALUE_MAX_LENGTH} from 'constants';

import InputField from 'components/InputField';

import {
  ChecklistOverviewWrapper,
  StyledForm,
  ButtonWrapper,
  StepsOverviewWrapper,
  StyledText,
  StyledSpinner,
  StyledButtonText,
  StyledWrapper,
  ClickBlocker
} from './styles';
import messages from './messages';
import SignaturePad from 'components/SignaturePad';

@inject('store')
@graphql(InstructionOverview, getInstructionOverviewOptions)
@graphql(SubmitChecklistAnswer, submitChecklistAnswerOptions)
@observer
class ChecklistOverview extends Component {
  state = {
    signature: {
      data: undefined,
      points: []
    }
  };
  constructor(props) {
    super(props);
    makeObservable(this);
  }

  @observable fullName = '';
  @action
  setFullName = fullName => (this.fullName = fullName);

  @observable extraFieldValue = '';
  @action
  setExtraFieldValue = extraFieldValue => (this.extraFieldValue = extraFieldValue);

  @observable showClickBlocker = false;
  @action setShowClickBlocker = val => (this.showClickBlocker = val);

  get showExtraField() {
    const {
      store: {
        platform: {checklistExtraFieldLabel, developmentFeatureFlags}
      }
    } = this.props;

    return Boolean(developmentFeatureFlags.checklistExtraField && checklistExtraFieldLabel);
  }

  onFormSubmit = async e => {
    e.preventDefault();

    this.setShowClickBlocker(false);
    const {
      instruction: {instruction},
      store: {
        checklist: {submitChecklist}
      },
      nextButton,
      submitChecklistAnswerMutation,
      intl: {formatMessage}
    } = this.props;

    try {
      await submitChecklist(
        this.fullName,
        this.state.signature.svg,
        submitChecklistAnswerMutation,
        nextButton,
        instruction,
        this.showExtraField ? this.extraFieldValue : ''
      );
    } catch (err) {
      Sentry.captureException(err);
      this.notification.error(formatMessage(messages.somethingWentWrong));
    }
  };

  componentDidMount = () => {
    const {
      store: {
        auth: {user}
      }
    } = this.props;

    const defaultUserName = user ? user.fullName : '';

    this.setFullName(defaultUserName);
  };

  onSignaturePadSubmit = signature => {
    this.setState({
      signature: {
        data: signature?.data,
        points: signature?.points,
        svg: signature?.svg
      }
    });
  };

  render() {
    const {
      instruction: {loading, error, instruction},
      intl: {formatMessage},
      store: {
        checklist: {
          isChecklistBeingSubmitted,
          isChecklistSubmitted,
          showErrorSubmittingModal,
          closeErrorSubmittingModal
        },
        platform: {hasChecklistsSignatureEnabled, checklistExtraFieldLabel}
      },
      StepsOverviewComponent,
      isDesktop
    } = this.props;

    const showSignature = hasChecklistsSignatureEnabled && instruction?.requireSignature;
    const {showExtraField} = this;
    const disableInputs = isChecklistSubmitted || isChecklistBeingSubmitted;
    const disableButton =
      (showSignature && !this.state.signature.data) ||
      !this.fullName ||
      isChecklistSubmitted ||
      isChecklistBeingSubmitted;
    if (loading) {
      return <Spinner />;
    }

    if (error) {
      return <StyledText>{formatMessage(messages.errorFetchingChecklist)}</StyledText>;
    }

    const {steps} = instruction;

    return (
      <ChecklistOverviewWrapper>
        <InfoModal
          isDesktop={isDesktop}
          isOpen={showErrorSubmittingModal}
          onClose={closeErrorSubmittingModal}
          onRightButtonClick={closeErrorSubmittingModal}
          rightButtonText={formatMessage(messages.ok)}
          subtitle={formatMessage(messages.errorSubmittingSubtitle)}
          title={formatMessage(messages.errorSubmittingTitle)}
        />
        <StyledText isDesktop={isDesktop}>{formatMessage(messages.checklistSummary).toUpperCase()}</StyledText>
        <StepsOverviewWrapper>
          <StepsOverviewComponent steps={steps} />
        </StepsOverviewWrapper>
        {/* Adding click blocker when the keyboard is open on phone/tablet
            we had an issue when clicking on submit button while the keyboard is open
            the event was somehow passed on the check cards insted of the button
        */}
        {this.showClickBlocker && (
          <ClickBlocker
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              this.setShowClickBlocker(false);
            }}
            onTouchStart={e => {
              e.preventDefault();
              e.stopPropagation();
              this.setShowClickBlocker(false);
            }}
          />
        )}
        <StyledForm
          onSubmit={e => {
            e.preventDefault();
            e.stopPropagation();
            this.onFormSubmit(e);
          }}
          isDesktop={isDesktop}
        >
          {showSignature && (
            <SignaturePad
              signature={this.state.signature}
              onSubmit={this.onSignaturePadSubmit}
              formatMessage={formatMessage}
            />
          )}
          <StyledWrapper isDesktop={isDesktop} showSignature={showSignature}>
            <InputField
              id="checklist-full-name"
              name="fullName"
              label={formatMessage(messages.yourName)}
              placeholder={formatMessage(messages.typeInFullName)}
              value={this.fullName}
              onChange={e => this.setFullName(e.target.value)}
              disabled={disableInputs}
              dataCy="user-checklist"
              onFocus={() => this.setShowClickBlocker(true)}
            />
            {showExtraField && (
              <InputField
                id="checklist-extra-field"
                name="extraField"
                label={checklistExtraFieldLabel}
                placeholder={formatMessage(messages.fillInField, {field: checklistExtraFieldLabel})}
                value={this.extraFieldValue}
                onChange={e => this.setExtraFieldValue(e.target.value)}
                disabled={disableInputs}
                maxLength={CHECKLIST_EXTRA_FIELD_VALUE_MAX_LENGTH}
                dataCy="extra-field-checklist"
                onFocus={() => this.setShowClickBlocker(true)}
              />
            )}
            <ButtonWrapper data-cy="submit-checklist" disabled={disableButton}>
              {isChecklistBeingSubmitted ? (
                <StyledSpinner size={25} color={commonPalette.white} />
              ) : (
                <StyledButtonText>{formatMessage(messages.submitResults)}</StyledButtonText>
              )}
            </ButtonWrapper>
          </StyledWrapper>
        </StyledForm>
      </ChecklistOverviewWrapper>
    );
  }
}

export default injectIntl(ChecklistOverview);
